/**
 * Motion + data hooks.
 *
 * The motion grammar is slow and heavy: 24px rise + fade over 400ms, staggered
 * 60ms, once only — never re-triggered on scroll-up. Counters run once, over
 * 1200ms, and always render the correct final value as the no-JS fallback.
 * Everything here is disabled under prefers-reduced-motion.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../three/capability';

export { usePrefersReducedMotion };

/** Reveal once when scrolled into view. */
export function useReveal(options = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      setRevealed(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect(); // once only
        }
      },
      { rootMargin: options.rootMargin || '0px 0px -10% 0px', threshold: options.threshold ?? 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [reduced, options.rootMargin, options.threshold]);

  return [ref, revealed];
}

/**
 * Count up to `target` once, on first view, over 1200ms with an ease-out.
 * Returns the target immediately under reduced motion, and the initial render is
 * always the final value so a no-JS / no-observer environment is still correct.
 */
export function useCountUp(target, { duration = 1200 } = {}) {
  const reduced = usePrefersReducedMotion();
  const ref = useRef(null);
  const [value, setValue] = useState(target);
  const started = useRef(false);

  useEffect(() => {
    if (reduced || started.current) return undefined;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || started.current) return;
      started.current = true;
      observer.disconnect();
      const start = performance.now();
      setValue(1);
      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - (1 - t) ** 3;
        // Never render 0 \u2014 the previous build shipped "0+ years" and it was the
        // most damaging bug on the site. The counter starts at 1 and lands
        // exactly on the target.
        setValue(Math.max(1, Math.round(target * eased)));
        if (t < 1) requestAnimationFrame(tick);
        else setValue(target); // never leave a partial value on screen
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration, reduced]);

  return [ref, value];
}

/** Scroll progress 0..1 for the 1px cyan bar under the sticky header. */
export function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      setProgress(max > 0 ? Math.min(doc.scrollTop / max, 1) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return progress;
}

/** Magnetic cursor pull on primary CTAs — desktop, pointer:fine only, <=6px. */
export function useMagnetic(max = 6) {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node || reduced) return undefined;
    if (!window.matchMedia || !window.matchMedia('(pointer: fine)').matches) return undefined;

    const onMove = (event) => {
      const rect = node.getBoundingClientRect();
      const dx = (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const dy = (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      node.style.transform = `translate(${Math.max(-1, Math.min(1, dx)) * max}px, ${
        Math.max(-1, Math.min(1, dy)) * max
      }px)`;
    };
    const onLeave = () => {
      node.style.transform = 'translate(0, 0)';
    };
    node.addEventListener('pointermove', onMove);
    node.addEventListener('pointerleave', onLeave);
    return () => {
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', onLeave);
    };
  }, [max, reduced]);

  return ref;
}

/** Small data hook with loading / error / empty states. */
export function useApi(fetcher, deps = [], initial = null) {
  const [state, setState] = useState({ data: initial, loading: true, error: null });
  const run = useCallback(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve(fetcher())
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: initial, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => run(), [run]);
  return { ...state, reload: run };
}
